import type {IHttpResponse, IPromise, ui} from 'angular';
import type {ModalWindowResult} from 'typings/portaro.fe.types';
import type {ModalResult, ModalWindowActions, ModalWindowSettings} from './modals.types';
import type {ActionResponse, ActionResponseWithMessage, ExceptionActionResponse} from 'typings/portaro.be.types';
import {isActionResponse, isActionResponseWithNestedResponses} from 'shared/utils/types-utils';
import {createAcknowledgeActionResponse, createInterruptActionResponse} from './modal-utils';
import {getActionResponse, getErrorMessage} from 'shared/utils/data-service-utils';
import {responseTypes} from 'shared/constants/portaro.constants';
import KpLink from 'shared/components/kp-link/KpLink.svelte';

export default class ModalWindow {

    private finishedResponse: ActionResponse = null; // response of the window based on the actions of the window
    private reopenWindow = false; // reopen window after closing it flag
    private windowInstance: ui.bootstrap.IModalInstanceService; // instance of modal window implementation

    constructor(private $uibModal: ui.bootstrap.IModalService, private TEMPLATES_PATH: string) {
    }

    public open(modalName: string, modalModel, modalWindowSettings?: ModalWindowSettings): Promise<ModalWindowResult> {
        return this.openManually(modalName, modalModel, modalWindowSettings);
    }

    public closeWindow() {
        this.windowInstance.dismiss({});
    }

    private async openManually(modalType: string, modalModel, modalWindowSettings?: ModalWindowSettings): Promise<ModalWindowResult> {
        if (!modalType) {
            throw new Error('Trying to create modal window with undefined or null modalType');
        }

        // objects passed to controller of modal window
        const resolveValues = {
            model() {
                return modalModel;
            },
            modalWindowActions(): ModalWindowActions {
                return {
                    // close modal window and resolve with rejection
                    cancel: () => {
                        modal.dismiss({}); // reject with empty UibModalResult
                    },
                    // close modal window and resolve itself
                    acknowledge: () => {
                        modal.close({}); // resolve with empty UibModalResult
                    },
                    // close modal window and reject itself with set reopen flag after promise is resolved, co caller can reopen it with new data
                    closeAndReopen: (closeAndReopenPromise: Promise<void>) => {
                        modal.dismiss({closeAndReopenPromise}); // reject with UibModalResult
                    },
                    // close modal window and reopen it again after promise is resolved, use data from resolved promise as new modalModel
                    reopen: (reopenPromise: Promise<any>) => {
                        modal.dismiss({reopenPromise}); // reject with UibModalResult
                    },
                    // close modal window and resolve after promise is resolved
                    submitPromise: (promise: IPromise<ActionResponse | IHttpResponse<ActionResponse>> | Promise<ActionResponse>) => {
                        modal.close({promise}); // resolve with UibModalResult
                    }
                };
            }
        };

        const modalWindowOptions: ui.bootstrap.IModalSettings = {
            keyboard: (modalModel && modalModel.nonDismissible) || false,
            backdrop: (modalModel && modalModel.nonDismissible) ? 'static' : false,
            size: modalWindowSettings?.size ?? 'lg',
            resolve: resolveValues
        };

        if (modalWindowSettings) {
            if (modalWindowSettings.isComponent) {
                modalWindowOptions.component = this.getModalWindowComponentName(modalType);
            }
        } else {
            modalWindowOptions.templateUrl = this.getModalWindowTemplateUrl(modalType);
            modalWindowOptions.controller = ['$scope', '$sce', 'model', 'modalWindowActions', 'TEMPLATES_PATH',
                ($scope, $sce, model, modalWindowActions, TEMPLATES_PATH) => {
                    $scope.$sce = $sce;
                    $scope.model = model;
                    $scope.modalWindowActions = modalWindowActions;
                    $scope.TEMPLATES_PATH = TEMPLATES_PATH;
                    $scope.kpLinkComponent = KpLink; // used in conv-PaymentGatewayRedirectResponse.ftl
                }
            ];
        }

        const modal = this.$uibModal.open(modalWindowOptions);
        this.windowInstance = modal;
        try {
            // choose correct handler based on the modal instance result
            const result: ModalResult = await modal.result;
            if (result.promise !== undefined) {
                await this.modalWindowSubmitPromiseHandler(result.promise);
            } else {
                this.modalWindowAcknowledgeHandler();
            }
        } catch (result) {
            if (result.closeAndReopenPromise) {
                await this.modalWindowCloseAndReopenHandler(result.closeAndReopenPromise);
            } else if (result.reopenPromise) {
                return this.modalWindowReopenHandler(result.reopenPromise, modalType, modalModel, modalWindowSettings);
            } else {
                this.modalWindowCancelHandler();
            }
        }
        return this.resolveModalWindow();
    }

    private async modalWindowReopenHandler(reopenPromise: Promise<any>, modalType: string, modalModel, modalWindowSettings: ModalWindowSettings) {
        try {
            const newModel = await reopenPromise;
            return this.openManually(modalType, newModel, modalWindowSettings);
        } catch (error) {
            if (isActionResponse(error) && error.responseType === responseTypes.INTERRUPT) {
                return this.openManually(modalType, modalModel, modalWindowSettings);
            }
            throw new Error(error);
        }
    }

    private modalWindowCancelHandler() {
        this.finishedResponse = createInterruptActionResponse();
    }

    private modalWindowAcknowledgeHandler() {
        this.finishedResponse = createAcknowledgeActionResponse();
    }

    private async modalWindowCloseAndReopenHandler(reopenPromise: Promise<void>) {
        await reopenPromise
            .then(() => this.reopenWindow = true)
            .catch((error) => {
                if (isActionResponse(error)) {
                    this.finishedResponse = error;
                } else {
                    this.finishedResponse = {
                        finished: true,
                        responseType: responseTypes.EXCEPTION,
                        simpleName: 'ModalReopenException',
                        message: getErrorMessage(error)
                    } as ExceptionActionResponse & ActionResponseWithMessage;
                }
            });
    }

    private async modalWindowSubmitPromiseHandler(submitPromise: IPromise<ActionResponse | IHttpResponse<ActionResponse>> | Promise<ActionResponse>) {
        try {
            const promise: Promise<ActionResponse | IHttpResponse<ActionResponse>> = Promise.resolve(submitPromise); // cast IPromise (angular) to Promise (ES6)
            const result = await promise;
            const response = getActionResponse(result);
            if (response.finished) {
                this.finishedResponse = response;
            }
        } catch (result) {
            const response = getActionResponse(result);
            if (response.finished) {
                this.finishedResponse = response;
            }
        }
    }

    private containAnySuccessful(response: ActionResponse) {
        if (isActionResponseWithNestedResponses(response)) {
            return response.finishedResponses.some(this.containAnySuccessful);
        }
        return response.responseType !== responseTypes.EXCEPTION &&
            response.responseType !== responseTypes.INTERRUPT;
    }

    private isSuccessful() {
        if (this.finishedResponse) {
            return this.containAnySuccessful(this.finishedResponse);
        }
        return false;
    }

    private resolveModalWindow(): Promise<ModalWindowResult> {
        const modalWindowResult: ModalWindowResult = {
            finishedResponse: this.finishedResponse,
            isSuccessful: this.isSuccessful(),
            reopen: this.reopenWindow
        };

        if (modalWindowResult.isSuccessful) {
            return Promise.resolve(modalWindowResult);
        }
        return Promise.reject(modalWindowResult);
    }

    private getModalWindowTemplateUrl(modalType: string): string {
        return `${this.TEMPLATES_PATH}conv-${modalType}.html`;
    }

    private getModalWindowComponentName(modalType: string): string {
        return `${modalType}Modal`;
    }
}
